import { ensureProtoProps } from '@rehaje/shared'

import { Iterator } from '../constants'
import { dispatch } from '../dispatch'
import { FatalDecoratorExistedError } from '../errors'
import { ObserveSetEvent } from '../event'
import { DEFINED } from '../flags'
import { addParent, removeParent } from '../operations/parent'
import { batch, bind, trigger } from '../operations/register'
import { createShallowProxy } from '../proxy'
import { evalContext, untrackDepth } from '../signal'
import { undispatchDepth } from '../undispatch'

import type { Annotation, AnnotationOptions, Reactive } from '../types'

function createDescriptor(
  proto: any,
  key: string,
  options?: AnnotationOptions
): PropertyDescriptor
function createDescriptor(
  proto: Reactive | undefined,
  key: string,
  options?: AnnotationOptions
): PropertyDescriptor {
  const observe = options?.observe !== false

  if (proto !== undefined) {
    const proxyKeys = ensureProtoProps(proto, '__rr_proxy_keys', [])
    if (!proxyKeys.includes(key)) proxyKeys.push(key)

    const reactiveKeys = ensureProtoProps(proto, '__rr_reactive_keys', [])
    if (!reactiveKeys.includes(key)) reactiveKeys.push(key)

    const observeKeys = ensureProtoProps(proto, '__rr_observe_keys', [])
    if (observe && !observeKeys.includes(key)) observeKeys.push(key)
  }

  return {
    configurable: false,
    enumerable: true,
    get(this: Reactive) {
      if (evalContext !== undefined || untrackDepth === 0) {
        if (
          this.__rr_proxies?.[key] === undefined ||
          this.__rr_values?.[key] !== undefined
        ) {
          this.__rr_proxies ??= {}
          this.__rr_proxies[key] = createShallowProxy(this.__rr_values?.[key])
          if (this.__rr_values?.[key] !== undefined)
            this.__rr_values[key] = undefined
        }
        bind(this, key)
      }
      const val = this.__rr_values?.[key] ?? this.__rr_proxies?.[key]
      if (this.__rr_flags & DEFINED && undispatchDepth === 0 && observe) {
        addParent(this, key, val)
      }
      return val
    },
    set(this: Reactive, value) {
      if (this.__rr_flags & DEFINED) {
        if (value === this.__rr_proxies?.[key]) return
        batch(() => {
          let event
          let setKey = key
          if (undispatchDepth === 0 && observe) {
            event = dispatch(new ObserveSetEvent(this, setKey, value))
            setKey = event.source.key
            value = event.source.value
          }
          this.__rr_proxies ??= {}
          const oldVal = this.__rr_proxies[setKey]
          if (value === oldVal) return
          this.__rr_proxies[setKey] = createShallowProxy(value)
          if (this.__rr_values?.[setKey] !== undefined)
            this.__rr_values[setKey] = undefined
          trigger(this, setKey)
          trigger(this, Iterator)
          if (undispatchDepth === 0 && observe) {
            event?.__r_runEffects()
            addParent(this, setKey, value)
            removeParent(this, setKey, oldVal)
          }
        })
      } else {
        this.__rr_values ??= {}
        this.__rr_values[key] = value
      }
    }
  }
}

export const shallow: Annotation<AnnotationOptions> = (value) => {
  return createShallowProxy(value)
}

shallow._name = 'shallow'

shallow._tsDecorator = (proto, key, descriptor, options) => {
  if (descriptor !== undefined)
    throw new FatalDecoratorExistedError(shallow._name, key)
  return createDescriptor(proto, key, options)
}

shallow._esAccessorDecorator = (context, options) => {
  const descriptor = createDescriptor(undefined, String(context.name), options)
  return {
    get: descriptor.get,
    init: descriptor.set,
    set: descriptor.set
  }
}
